import { Immutable } from "immer";
import React from "react";

import { IProduct } from "../../../models/catalogue";
import { ISafeHTML } from "../../../models/nominals";
import { ICompareTile } from "../../../models/product-compare";
import {
    IGA4ListMetadata,
    trackViewItemListEvent,
} from "../../../utils/analytics";
import {
    buildProductCompareListMetadata,
    buildTrackedProductsSignature,
    resolveProductCompareTrackedProducts,
} from "../analytics.shared";

interface IUseViewItemListTrackingResult {
    listMetadata: Immutable<IGA4ListMetadata>;
    viewedProducts: Immutable<readonly IProduct[]>;
}

/**
 * Computes GA4 list metadata and resolved products, fires a view_item_list
 * event whenever they change, and returns both values for use in
 * `<ProductCompareAnalyticsProvider>`.
 *
 * @param tiles           Already-resolved tiles to track. Callers are
 *                        responsible for filtering/windowing before passing in.
 * @param priceSelectValue Selected size/variant key. Pass `null` to skip
 *                        variant resolution (e.g. when no size selector exists).
 * @param header          Section heading used to derive the GA4 list name/ID.
 * @param fallbackName    Used when `header` is absent or empty.
 * @param requiredCount   When provided, the event is suppressed if this value
 *                        equals 0. Pass `selectedTileIDs.length` when the
 *                        component requires at least one selection before
 *                        tracking.
 */
export const useViewItemListTracking = (
    tiles: readonly ICompareTile[],
    priceSelectValue: string | null,
    header: string | ISafeHTML | null | undefined,
    fallbackName: string,
    requiredCount?: number,
): IUseViewItemListTrackingResult => {
    const listMetadata = React.useMemo(
        () => buildProductCompareListMetadata(header, fallbackName),
        [header, fallbackName],
    );

    const viewedProducts = React.useMemo(
        () => resolveProductCompareTrackedProducts(tiles, priceSelectValue),
        [tiles, priceSelectValue],
    );

    const viewedProductsSignature = React.useMemo(
        () => buildTrackedProductsSignature(viewedProducts),
        [viewedProducts],
    );

    const lastFiredSignatureRef = React.useRef<string | null>(null);

    React.useEffect(() => {
        if (requiredCount === 0) {
            lastFiredSignatureRef.current = null;
            return;
        }
        if (viewedProductsSignature === lastFiredSignatureRef.current) {
            return;
        }
        lastFiredSignatureRef.current = viewedProductsSignature;
        trackViewItemListEvent(viewedProducts, listMetadata);
    }, [listMetadata, requiredCount, viewedProducts, viewedProductsSignature]);

    return { listMetadata, viewedProducts };
};
